/** The minimap beside the mission header: where the ambulance is, the road it has driven, and the road left
 *  to its receiving facility.
 *
 *  Positions are its device's own GPS reports, one every ~10-12 s (status packets). Between two reports the
 *  marker glides along the road (the route it was given, else a straight line) over as long as the reports
 *  are apart, so it moves continuously, one report behind. Tiles are the ones the rest of Sentio uses
 *  (MAP_TILE_URL); the road comes from the Mission Agent's routing service. */

import { type ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import { AttributionControl, MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";
import { Box, Chip, Paper, Tooltip as Hint, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { MAP_ATTRIBUTION, MAP_TILE_URL } from "../../../utils/constants/constants";
import { along, lengths, stretchBetween, type LatLng } from "../geo";
import { ramp } from "../theme";
import type { MissionTrack } from "../useMissionTrack";

/** A glide between two reports takes as long as they came apart, within these bounds (ms). */
const MIN_GLIDE_MS = 800;
const MAX_GLIDE_MS = 15000;
/** Closer than this to the facility, the ambulance is there. */
const AT_FACILITY_M = 100;
/** A position older than this is called out as stale. */
const STALE_MS = 2 * 60 * 1000;
/** After the user pans or zooms, the map leaves the view alone for this long. */
const HANDS_OFF_MS = 20000;
/** Where the map opens before there is anything to show (Trondheim). */
const DEFAULT_CENTER: LatLng = [63.43, 10.39];

const Root = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  overflow: "hidden",
  "& .leaflet-container": { flex: 1, minHeight: 280, fontFamily: theme.typography.fontFamily },
  // The map data's credit (OpenStreetMap's licence requires it): small and faint, without Leaflet's own.
  "& .leaflet-control-attribution": {
    fontSize: "0.625rem",
    lineHeight: 1.4,
    padding: "0 4px",
    background: "rgba(255, 255, 255, 0.55)",
    color: theme.palette.text.disabled,
    "& a": { color: "inherit" },
  },
}));

const Heading = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  padding: theme.spacing(1.25, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

/** The one line above the map: the facility and the ETA. */
const Line = styled(Typography)(({ theme }) => ({
  fontSize: "0.875rem",
  lineHeight: 1.45,
  color: theme.palette.text.secondary,
  minWidth: 0,
  "& b": { color: theme.palette.text.primary, fontWeight: 600 },
}));

/** "+2 more": the other nearby facilities, listed on hover. */
const More = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  borderBottom: `1px dotted ${theme.palette.text.secondary}`,
  cursor: "help",
  whiteSpace: "nowrap",
}));

const ambulanceIcon = L.divIcon({
  className: "",
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  html: `<div style="position:relative;width:30px;height:30px">
    <div class="ma-heading" style="position:absolute;inset:0;transition:transform 300ms linear">
      <div style="position:absolute;left:10px;top:-3px;width:0;height:0;border-left:5px solid transparent;
        border-right:5px solid transparent;border-bottom:9px solid ${ramp.primary[700]}"></div>
    </div>
    <div style="position:absolute;left:6px;top:6px;width:18px;height:18px;border-radius:50%;
      background:${ramp.primary[500]};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></div>
  </div>`,
});

const facilityIcon = L.divIcon({
  className: "",
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  html: `<div style="width:24px;height:24px;border-radius:6px;background:${ramp.accent[600]};color:#fff;
    display:flex;align-items:center;justify-content:center;font:700 14px/1 sans-serif;
    border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)">H</div>`,
});

/** A facility near the ambulance when none is selected: outlined, so it does not read as the destination. */
const nearestIcon = L.divIcon({
  className: "",
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  html: `<div style="width:20px;height:20px;border-radius:5px;background:#fff;color:${ramp.accent[600]};
    display:flex;align-items:center;justify-content:center;font:700 12px/1 sans-serif;
    border:2px solid ${ramp.accent[600]};box-shadow:0 1px 3px rgba(0,0,0,.3)">H</div>`,
});

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** The ambulance marker, the road behind it (grey) and the stretch it is gliding along ahead of it (blue).
 *  Drawn with Leaflet directly: they change every animation frame. */
function Ambulance({ track }: { track: MissionTrack }) {
  const map = useMap();
  const marker = useRef<L.Marker | null>(null);
  const behind = useRef<L.Polyline | null>(null);
  const ahead = useRef<L.Polyline | null>(null);
  const shown = useRef<LatLng | null>(null); // where the marker is now
  const driven = useRef<LatLng[]>([]); // the road behind it, up to the last road vertex it passed
  const road = useRef<LatLng[] | null>(null); // the route it was last given (starts at the previous report)
  const reported = useRef(0); // how many reports it has been sent towards
  const lastReceived = useRef<number | null>(null);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    behind.current = L.polyline([], { color: ramp.neutral[400], weight: 3, opacity: 0.9 }).addTo(map);
    ahead.current = L.polyline([], { color: ramp.primary[500], weight: 4, opacity: 0.8 }).addTo(map);
    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      behind.current?.remove();
      ahead.current?.remove();
      marker.current?.remove();
    };
  }, [map]);

  useEffect(() => {
    const points: LatLng[] = track.positions.map((p) => [p.lat, p.lng]);
    const routeNow = track.route ? track.route.coordinates : null;
    if (points.length === 0) return;
    if (!marker.current) {
      // First sight: everything so far is behind it, no glide.
      const here = points[points.length - 1];
      marker.current = L.marker(here, { icon: ambulanceIcon, keyboard: false, zIndexOffset: 1000 }).addTo(map);
      marker.current.bindTooltip("Ambulance (last GPS report)", { direction: "top", offset: [0, -14] });
      shown.current = here;
      driven.current = points;
      behind.current?.setLatLngs(points);
      reported.current = points.length;
      road.current = routeNow;
      lastReceived.current = track.receivedAt;
      return;
    }
    if (points.length <= reported.current) {
      road.current = routeNow ?? road.current;
      return;
    }

    // New reports: glide from where the marker is through each of them, along the road where they lie on it.
    const targets = points.slice(reported.current);
    reported.current = points.length;
    let path: LatLng[] = [shown.current ?? targets[0]];
    for (const target of targets) {
      path = [...path, ...stretchBetween(road.current, path[path.length - 1], target).slice(1)];
    }
    road.current = routeNow;
    const gap = track.receivedAt && lastReceived.current ? track.receivedAt - lastReceived.current : MIN_GLIDE_MS;
    lastReceived.current = track.receivedAt;
    const duration = reducedMotion() ? 0 : Math.max(MIN_GLIDE_MS, Math.min(MAX_GLIDE_MS, gap));
    const cumulative = lengths(path);
    const base = [...driven.current, ...(shown.current ? [shown.current] : [])];
    const started = performance.now();
    if (frame.current) cancelAnimationFrame(frame.current);

    const step = (now: number) => {
      const share = duration === 0 ? 1 : (now - started) / duration;
      const { at, heading, passed } = along(path, cumulative, share);
      shown.current = at;
      marker.current?.setLatLng(at);
      const arrow = marker.current?.getElement()?.querySelector<HTMLElement>(".ma-heading");
      if (arrow && heading !== null) arrow.style.transform = `rotate(${heading}deg)`;
      // Kept every frame: a newer report cuts a glide short, and the next one carries on from here.
      driven.current = share >= 1 ? [...base, ...path.slice(1)] : [...base, ...path.slice(1, passed + 1)];
      behind.current?.setLatLngs(share >= 1 ? driven.current : [...driven.current, at]);
      ahead.current?.setLatLngs(share >= 1 ? [] : [at, ...path.slice(passed + 1)]);
      if (share < 1) {
        frame.current = requestAnimationFrame(step);
        return;
      }
      frame.current = null;
    };
    frame.current = requestAnimationFrame(step);
  }, [map, track.positions, track.route, track.receivedAt]);

  return null;
}

/** Everything the journey covers: the road driven, the road left and the facility. */
function journeyPoints(track: MissionTrack): LatLng[] {
  const points: LatLng[] = track.positions.map((p) => [p.lat, p.lng]);
  if (track.facility) points.push([track.facility.lat, track.facility.lng]);
  else if (track.nearest.length) points.push([track.nearest[0].lat, track.nearest[0].lng]);
  if (track.route && !track.journeyOver) points.push(...track.route.coordinates);
  return points;
}

/** Keeps the whole journey in view: once there is something to show, when the facility or the road changes,
 *  when the map's box settles or resizes (the grid lays out after Leaflet measured it), and when the
 *  ambulance would leave the view. Not for a while after the user panned or zoomed. */
function Framing({ track }: { track: MissionTrack }) {
  const map = useMap();
  const latest = useRef(track);
  latest.current = track;
  const framed = useRef<string | null>(null);
  const handsOffUntil = useRef(0);

  const fit = useCallback(() => {
    const points = journeyPoints(latest.current);
    if (points.length === 0) return false;
    map.invalidateSize();
    if (points.length === 1) map.setView(points[0], 14, { animate: false });
    else map.fitBounds(L.latLngBounds(points), { padding: [24, 24], maxZoom: 15, animate: false });
    return true;
  }, [map]);

  useEffect(() => {
    // The user's own pan or zoom (pointer or wheel; fitBounds itself fires zoom events, so not those).
    const container = map.getContainer();
    const leaveAlone = () => {
      handsOffUntil.current = Date.now() + HANDS_OFF_MS;
    };
    container.addEventListener("pointerdown", leaveAlone);
    container.addEventListener("wheel", leaveAlone, { passive: true });
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
      if (framed.current && Date.now() > handsOffUntil.current) fit();
    });
    observer.observe(container);
    return () => {
      container.removeEventListener("pointerdown", leaveAlone);
      container.removeEventListener("wheel", leaveAlone);
      observer.disconnect();
    };
  }, [map, fit]);

  const key = [
    track.positions.length > 0,
    track.facility?.id ?? `nearest:${track.nearest[0]?.id ?? ""}`,
    Boolean(track.route && !track.journeyOver),
  ].join("|");
  useEffect(() => {
    if (framed.current !== key && fit()) framed.current = key;
  }, [key, fit]);

  const last = track.positions[track.positions.length - 1];
  useEffect(() => {
    if (!last || !framed.current || Date.now() < handsOffUntil.current) return;
    if (!map.getBounds().contains([last.lat, last.lng])) fit();
  }, [map, fit, last]);

  return null;
}

function minutes(seconds: number): string {
  const m = Math.max(1, Math.round(seconds / 60));
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
}

/** "Selected receiving facility: <b>name</b> (ETA ~12 min · 7.4 km by road)"; with none selected, the
 *  facilities nearest by drive time: "No receiving facility selected · Nearest by road: <b>name</b> (ETA ~14
 *  min) · other ~38 min" - a fact about the roads, not a choice made for the crew. */
function summary(track: MissionTrack): { line: ReactNode; text: string; stale: string | null } {
  const last = track.positions[track.positions.length - 1];
  const fixAt = track.fixAt ?? last?.at;
  const age = fixAt ? Date.now() - new Date(fixAt).getTime() : null;
  const stale = age !== null && age > STALE_MS && !track.journeyOver ? `GPS ${minutes(age / 1000)} old` : null;
  if (!track.facility) {
    if (!last) return { line: "Waiting for the device's GPS position", text: "Waiting for the device's GPS position", stale };
    const [first, ...others] = track.nearest;
    if (!first) return { line: "No receiving facility selected", text: "No receiving facility selected", stale };
    const how = first.source === "straight" ? "Nearest (straight-line estimate)" : "Nearest by road";
    const eta = `ETA ~${minutes(first.duration_s)}`;
    const list = (
      <Box sx={{ fontSize: "0.8125rem", lineHeight: 1.6 }}>
        <div>No receiving facility selected yet. Also nearby:</div>
        {others.map((n) => (
          <div key={n.id}>
            {n.name} · ~{minutes(n.duration_s)}
            {n.source === "straight" ? " (straight-line estimate)" : " by road"}
          </div>
        ))}
      </Box>
    );
    return {
      line: (
        <>
          {how}: <b>{first.name}</b> ({eta})
          {others.length ? (
            <>
              {" · "}
              <Hint title={list} arrow placement="bottom-start">
                <More tabIndex={0}>+{others.length} more</More>
              </Hint>
            </>
          ) : null}
        </>
      ),
      text: `No receiving facility selected. ${how}: ${first.name} (${eta})`,
      stale,
    };
  }
  const route = track.route;
  let detail: string | null = null;
  if (track.journeyOver) detail = "arrived";
  else if (!last) detail = "no GPS position yet";
  else if (route && route.distance_m < AT_FACILITY_M) detail = "at the facility";
  else if (route) {
    const km = (route.distance_m / 1000).toFixed(route.distance_m < 10000 ? 1 : 0);
    detail =
      route.source === "straight"
        ? `ETA ~${minutes(route.duration_s)} · ${km} km, straight-line estimate`
        : `ETA ~${minutes(route.duration_s)} · ${km} km by road`;
  }
  const text = `Selected receiving facility: ${track.facility.name}${detail ? ` (${detail})` : ""}`;
  const line = (
    <>
      Selected receiving facility: <b>{track.facility.name}</b>
      {detail ? ` (${detail})` : null}
    </>
  );
  return { line, text, stale: track.journeyOver ? null : stale };
}

export function MissionMap({ track }: { track: MissionTrack }) {
  const { line, text, stale } = summary(track);
  const route = track.route;
  // Dashed: a straight-line estimate, or the road to the nearest facility (none selected).
  const dashed = route?.source === "straight" || !track.facility;
  const routeStyle = useMemo(
    () => ({ color: ramp.primary[500], weight: 4, opacity: dashed ? 0.65 : 0.8, dashArray: dashed ? "6 8" : undefined }),
    [dashed],
  );
  return (
    <Root>
      <Heading>
        <Line title={text}>{line}</Line>
        {stale ? <Chip size="small" color="warning" label={stale} sx={{ flexShrink: 0 }} /> : null}
      </Heading>
      <MapContainer center={DEFAULT_CENTER} zoom={10} scrollWheelZoom={false} attributionControl={false}>
        <AttributionControl position="bottomright" prefix={false} />
        <TileLayer attribution={MAP_ATTRIBUTION} url={MAP_TILE_URL} />
        <Framing track={track} />
        {route && !track.journeyOver ? (
          <Polyline key={`${route.source}|${dashed}`} positions={route.coordinates} pathOptions={routeStyle} />
        ) : null}
        {track.facility ? (
          <Marker position={[track.facility.lat, track.facility.lng]} icon={facilityIcon} keyboard={false}>
            <Tooltip direction="top" offset={[0, -12]}>
              {track.facility.name}
            </Tooltip>
          </Marker>
        ) : null}
        {!track.facility && !track.journeyOver
          ? track.nearest.map((n) => (
              <Marker key={n.id} position={[n.lat, n.lng]} icon={nearestIcon} keyboard={false}>
                <Tooltip direction="top" offset={[0, -10]}>
                  {n.name} · ~{minutes(n.duration_s)}
                  {n.source === "straight" ? " (straight-line estimate)" : " by road"}
                </Tooltip>
              </Marker>
            ))
          : null}
        <Ambulance track={track} />
      </MapContainer>
    </Root>
  );
}
